import { Controller } from "stimulus"

// The call-task dialog's assignee dropdown: manager_picker_controller's menu, single-select, on a
// list rendered into the page (no paging: the eligible list is short).
export default class extends Controller {
  static targets = ["input", "avatar", "label", "search", "option", "status"]

  declare readonly inputTarget: HTMLInputElement
  declare readonly avatarTarget: HTMLElement
  declare readonly labelTarget: HTMLElement
  declare readonly searchTarget: HTMLInputElement
  declare readonly optionTargets: HTMLElement[]
  declare readonly statusTarget: HTMLElement

  private readonly onDocumentClick = (event: MouseEvent): void => {
    if (!this.element.contains(event.target as Node)) this.close()
  }

  connect(): void {
    this.sync()
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect(): void {
    document.removeEventListener("click", this.onDocumentClick)
  }

  // Re-read the hidden input after the dialog rewrote it. An id that isn't on the list is kept
  // only when the dialog names it: an existing task's assignee who is no longer eligible.
  sync(): void {
    const id = this.inputTarget.value
    const option = this.optionTargets.find(el => el.dataset.value === id)
    const keptEmail = this.inputTarget.dataset.email || ""

    if (option) this.show(option.dataset.email || "", option.dataset.ini || "", option.dataset.color || "")
    else if (id && keptEmail) this.show(`${keptEmail} (inactive)`, this.inputTarget.dataset.ini || "?", "")
    else {
      this.inputTarget.value = ""
      this.show("", "", "")
    }

    this.optionTargets.forEach(el => {
      const active = !!id && el.dataset.value === id
      el.classList.toggle("active", active)
      el.setAttribute("aria-selected", String(active))
    })

    this.searchTarget.value = ""
    this.filter("")
    this.close()
  }

  // The click is left to bubble, so an open due-date calendar closes as the menu opens.
  toggle(event: Event): void {
    event.preventDefault()

    if (this.isOpen()) this.close()
    else this.open()
  }

  // Each open starts with the whole list: Esc in a search box clears its text without an input
  // event, which would otherwise leave the old filter applied to an empty box.
  open(): void {
    this.searchTarget.value = ""
    this.filter("")
    this.element.classList.add("open")
    this.triggerEl()?.setAttribute("aria-expanded", "true")
    this.searchTarget.focus()
  }

  close(): void {
    if (!this.isOpen()) return

    this.element.classList.remove("open")
    this.triggerEl()?.setAttribute("aria-expanded", "false")
  }

  // Closes the menu only; the dialog around it stays open.
  closeOnEscape(event: KeyboardEvent): void {
    if (event.key !== "Escape" || !this.isOpen()) return

    event.stopPropagation()
    this.close()
    this.triggerEl()?.focus()
  }

  search(): void {
    this.filter(this.searchTarget.value)
  }

  pick(event: Event): void {
    const option = (event.target as HTMLElement).closest<HTMLElement>("[data-task-assignee-picker-target='option']")
    if (!option) return

    this.inputTarget.value = option.dataset.value || ""
    delete this.inputTarget.dataset.email
    delete this.inputTarget.dataset.ini
    this.sync()
    this.triggerEl()?.focus()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  private isOpen(): boolean {
    return this.element.classList.contains("open")
  }

  private triggerEl(): HTMLElement | null {
    return this.element.querySelector<HTMLElement>("[data-task-assignee-picker-trigger]")
  }

  private show(label: string, ini: string, color: string): void {
    this.labelTarget.textContent = label || "Choose someone"
    this.labelTarget.title = label
    this.element.classList.toggle("has-val", label !== "")

    this.avatarTarget.hidden = label === ""
    this.avatarTarget.textContent = ini
    this.avatarTarget.style.background = color
  }

  private filter(raw: string): void {
    const query = raw.trim().toLowerCase()
    let shown = 0

    this.optionTargets.forEach(el => {
      const match = !query || `${el.dataset.email || ""} ${el.dataset.name || ""}`.toLowerCase().includes(query)
      el.hidden = !match
      if (match) shown += 1
    })

    const message = shown > 0 ? ""
      : this.optionTargets.length === 0 ? "No eligible assignees."
        : "No one matches that search."
    this.statusTarget.textContent = message
    this.statusTarget.hidden = message === ""
  }
}
